import type { ISupportCountryItem, ISupportLangItem } from '@/common';
import type { IUserProfileRes } from '@/interface/account/IGetUserProfile';
import React from 'react';
import * as sitePath from '@/utils/function/sitePath';

export interface IDropdownMenuItem {
    title: string;
    link?: string;
    target?: string;
    onClick?: () => void;
    children?: Array<Omit<IDropdownMenuItem, 'children'>>;
}

/**
 * 头部nav按钮信息
 */
export interface IHeaderNavItemData {
    title: string;
    /**
     * 链接
     */
    link?: string;
    /**
     * @desc 下拉菜单
     * @default []
     */
    menus?: IDropdownMenuItem[];
    /**
     * @desc 是否显示徽标
     * @default false
     */
    badge?: boolean;
}
/**
 * 头部操作按钮信息
 */
export interface IHeaderActionItemData {
    iconName: string;
    /**
     * @desc 图标右侧文字
     * @default ""
     */
    text?: string;
    /**
     * @desc 文字大小
     * @default "20px"
     */
    fontSize?: string;
}

/**
 * 头部左侧导航按钮列表
 */
export const headerNavList: IHeaderNavItemData[] = [
    {
        title: 'nav-cloud',
        link: sitePath.getCloudPath(),
    },
    {
        title: 'nav-support',
        menus: [
            {
                title: 'menu-support-center',
                link: sitePath.getSupportPath(),
                target: '_blank',
            },
            {
                title: 'menu-download-center',
                children: [
                    {
                        title: 'menu-download-app-client',
                        link: sitePath.getOfficialSitePath('/software-and-manual/'),
                        target: '_blank',
                    },
                    {
                        title: 'menu-download-firmware',
                        link: sitePath.getOfficialSitePath('/download-center/'),
                        target: '_blank',
                    },
                    {
                        title: 'menu-download-manual',
                        link: sitePath.getOfficialSitePath('/download-center/?tab=guide-and-others'),
                        target: '_blank',
                    },
                ],
            },
            {
                title: 'menu-blog',
                link: sitePath.getOfficialSitePath('/blog/'),
            },
            {
                title: 'menu-contact-us',
                link: sitePath.getOfficialSitePath('/contact-us/'),
            },
        ],
    },
    {
        title: 'nav-deals',
        badge: true,
        menus: [
            {
                title: 'menu-flash-sale',
                link: sitePath.getOfficialSitePath('/flash-sale/'),
            },
            {
                title: 'menu-shop-refurbished',
                link: sitePath.getOfficialSitePath('/refurbished-products/'),
            },
            {
                title: 'menu-reolink-live',
                link: sitePath.getOfficialSitePath('/lp/reolink-live/'),
            },
        ],
    },
    {
        title: 'partner',
        link: sitePath.getOfficialSitePath('/distributor-reseller/'),
    },
];
/**
 * 头部右侧操作按钮列表
 */
export const headerActionList: IHeaderActionItemData[] = [
    {
        iconName: 'icon-search',
    },
    {
        iconName: 'icon-user',
    },
    {
        iconName: 'icon-shopping-bag',
    },
];

export interface IHeaderContext {
    isShowSearch: boolean;
    setIsShowSearch?: (visible: boolean) => void;
    langList: ISupportLangItem[];
    countryList: ISupportCountryItem[];
    userPrefile?: IUserProfileRes['me'];
    lang: string;
    country: string;
    countryTitle?: string;
    langTitle?: string;
    getTextByKey: (key: string) => string;
}

export const HeaderContext = React.createContext<IHeaderContext>({
    isShowSearch: false,
    langList: [],
    countryList: [],
    lang: 'en',
    country: '_',
    getTextByKey: (key: string) => key,
});

export const showLiveCountryList = ['US'];
export const hideCloudCountryList = ['US', '__'];

/**
 * @desc 分类对应的链接
 */
export const categoryKeyUrlMap: Record<string, { more: string; comparison: string; }> = {
    'home-hub': {
        more: sitePath.getStorePath('/home-hub-series/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#home-hub'),
    },
    'battery-powered-security-cameras': {
        more: sitePath.getStorePath('/battery-security-cameras/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#wifi-free'),
    },
    'dual-lens-cameras': {
        more: sitePath.getStorePath('/dual-lens-cameras/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#dual-lens-camera'),
    },
    'poe-wifi-security-camera-systems': {
        more: sitePath.getStorePath('/security-camera-systems/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#systems'),
    },
    'wifi-security-cameras': {
        more: sitePath.getStorePath('/wifi-security-cameras/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#wifi'),
    },
    'poe-ip-cameras-nvrs': {
        more: sitePath.getStorePath('/poe-ip-cameras/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#poe'),
    },
    'video-doorbells': {
        more: sitePath.getStorePath('/video-doorbells/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#video-doorbells'),
    },
    accessories: {
        more: sitePath.getStorePath('/accessories/'),
        comparison: sitePath.getOfficialSitePath('/comparison/#accessories'),
    },
};
